import React, { useState, useEffect } from 'react';
// FaUsers and FaInfoCircle are used in the audience-targeting section below.
// They were missing from this import, which threw a ReferenceError and blanked
// the page the moment "Add Form to Curriculum" was opened.
import {
    FaPlus, FaTrash, FaClock, FaFileAlt, FaTimes, FaCalendarAlt, FaUsers, FaInfoCircle
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { describeSubmissionPolicy, getFormTypeLabel } from '../utils/formTypes';
import './CurriculumFormManager.css';
import LoadingSpinner from './LoadingSpinner';

const CurriculumFormManager = ({ 
    curriculumForms = [], 
    onFormsChange, 
    canSkip = true, 
    isExpanded = true,
    onToggle 
}) => {
    const [formTemplates, setFormTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [newFormData, setNewFormData] = useState({
        form_template_id: '',
        schedule: {
            type: 'one_time',
            start_date: '',
            end_date: '',
            repeat_every_days: null
        },
        is_active: true,
        // NEW: Audience targeting
        target_type: 'form_type',
        target_user_ids: [],
        target_filters: {
            account_types: [],
            ranks: [],
            specialities: [],
            institutions: []
        }
    });

    useEffect(() => {
        fetchFormTemplates();
    }, []);

    const fetchFormTemplates = async () => {
        try {
            setLoading(true);
            // Fetch available form templates
            const response = await axiosInstance.get('/form-builder/templates/');
            // Filter active templates only
            const activeTemplates = response.data.filter(template => 
                template.is_active && template.is_published
            );
            setFormTemplates(activeTemplates);
        } catch (err) {
            console.error('Error fetching form templates:', err);
            setError('Failed to load form templates');
        } finally {
            setLoading(false);
        }
    };

    const handleAddForm = () => {
        if (!newFormData.form_template_id) {
            alert('Please select a form template');
            return;
        }
        if (!newFormData.schedule.start_date) {
            alert('Please set a start date for the form');
            return;
        }
        if (!newFormData.schedule.end_date) {
            alert('Please set an end date for the form');
            return;
        }
        if (new Date(newFormData.schedule.start_date) >= new Date(newFormData.schedule.end_date)) {
            alert('End date must be after start date');
            return;
        }
        
        // Validate audience targeting
        if (newFormData.target_type === 'explicit' && newFormData.target_user_ids.length === 0) {
            alert('Please specify at least one user ID for explicit targeting');
            return;
        }
        if (newFormData.target_type === 'filters') {
            const hasFilters = Object.values(newFormData.target_filters).some(arr => arr.length > 0);
            if (!hasFilters) {
                alert('Please specify at least one filter for filter-based targeting');
                return;
            }
        }

        // Check if this template is already added
        const isAlreadyAdded = curriculumForms.some(form => 
            form.form_template_id === newFormData.form_template_id
        );
        if (isAlreadyAdded) {
            alert('This form template is already added to the curriculum');
            return;
        }

        const selectedTemplate = formTemplates.find(t => t._id === newFormData.form_template_id);
        const formToAdd = {
            ...newFormData,
            template_name: selectedTemplate?.name || 'Unknown Template',
            template_type: selectedTemplate?.form_type || 'general',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };

        const updatedForms = [...curriculumForms, formToAdd];
        onFormsChange(updatedForms);

        // Reset form
        setNewFormData({
            form_template_id: '',
            schedule: {
                type: 'one_time',
                start_date: '',
                end_date: '',
                repeat_every_days: null
            },
            is_active: true,
            target_type: 'form_type',
            target_user_ids: [],
            target_filters: {
                account_types: [],
                ranks: [],
                specialities: [],
                institutions: []
            }
        });
        setShowAddForm(false);
    };

    const handleRemoveForm = (index) => {
        const updatedForms = curriculumForms.filter((_, i) => i !== index);
        onFormsChange(updatedForms);
    };

    const handleScheduleChange = (index, scheduleData) => {
        const updatedForms = [...curriculumForms];
        updatedForms[index] = {
            ...updatedForms[index],
            schedule: { ...updatedForms[index].schedule, ...scheduleData },
            updated_at: new Date().toISOString()
        };
        onFormsChange(updatedForms);
    };

    const handleActiveToggle = (index) => {
        const updatedForms = [...curriculumForms];
        updatedForms[index] = {
            ...updatedForms[index],
            is_active: !updatedForms[index].is_active,
            updated_at: new Date().toISOString()
        };
        onFormsChange(updatedForms);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const selectedTemplateForAdd = formTemplates.find(t => t._id === newFormData.form_template_id) || null;

    const getScheduleDisplay = (schedule) => {
        if (!schedule) return 'No schedule';
        const type = schedule.type === 'one_time' ? 'One Time' : 'Recurring';
        const start = formatDate(schedule.start_date);
        const end = formatDate(schedule.end_date);
        const recurring = schedule.type === 'recurring' && schedule.repeat_every_days 
            ? ` (Every ${schedule.repeat_every_days} days)` 
            : '';
        return `${type}: ${start} - ${end}${recurring}`;
    };

    if (!isExpanded) {
        return (
            <div className="curriculum-form-manager collapsed">
                <div className="section-header" onClick={onToggle}>
                    <div className="header-content">
                        <div className="header-icon">
                            <FaFileAlt />
                        </div>
                        <div className="header-text">
                            <h3>Form Management</h3>
                            <span className="form-count">
                                {curriculumForms.length} form{curriculumForms.length !== 1 ? 's' : ''} configured
                            </span>
                        </div>
                    </div>
                    <div className="expand-arrow">›</div>
                </div>
            </div>
        );
    }

    return (
        <div className="curriculum-form-manager">
            <div className="section-header" onClick={onToggle}>
                <div className="header-content">
                    <div className="header-icon">
                        <FaFileAlt />
                    </div>
                    <div className="header-text">
                        <h3>Form Management</h3>
                        <p>Add and configure forms for this curriculum with specific schedules</p>
                    </div>
                </div>
                <div className="expand-arrow expanded">‹</div>
            </div>

            <div className="section-content">
                {error && (
                    <div className="error-message">
                        <span>{error}</span>
                        <button onClick={fetchFormTemplates} className="retry-btn">
                            Retry
                        </button>
                    </div>
                )}

                {/* Current Forms */}
                {curriculumForms.length > 0 && (
                    <div className="current-forms">
                        <h4>Configured Forms ({curriculumForms.length})</h4>
                        <div className="forms-list">
                            {curriculumForms.map((form, index) => (
                                <div key={index} className="form-item">
                                    <div className="form-header">
                                        <div className="form-info">
                                            <div className="form-name">
                                                <FaFileAlt className="form-icon" />
                                                <span>{form.template_name || 'Unknown Template'}</span>
                                                <span className="form-type">({getFormTypeLabel(form.template_type)})</span>
                                            </div>
                                            <div className="form-status">
                                                <label className="active-toggle">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.is_active}
                                                        onChange={() => handleActiveToggle(index)}
                                                    />
                                                    <span className="toggle-slider"></span>
                                                    Active
                                                </label>
                                            </div>
                                        </div>
                                        <button 
                                            className="remove-form-btn"
                                            onClick={() => handleRemoveForm(index)}
                                            title="Remove form"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>

                                    <div className="form-schedule">
                                        <div className="schedule-display">
                                            <FaClock className="schedule-icon" />
                                            <span>{getScheduleDisplay(form.schedule)}</span>
                                        </div>
                                        
                                        <div className="schedule-controls">
                                            <div className="schedule-row">
                                                <label>Type:</label>
                                                <select
                                                    value={form.schedule?.type || 'one_time'}
                                                    onChange={(e) => handleScheduleChange(index, { type: e.target.value })}
                                                >
                                                    <option value="one_time">One Time</option>
                                                    <option value="recurring">Recurring</option>
                                                </select>
                                            </div>
                                            
                                            <div className="schedule-row">
                                                <label>Start:</label>
                                                <input
                                                    type="datetime-local"
                                                    value={form.schedule?.start_date || ''}
                                                    onChange={(e) => handleScheduleChange(index, { start_date: e.target.value })}
                                                />
                                            </div>
                                            
                                            <div className="schedule-row">
                                                <label>End:</label>
                                                <input
                                                    type="datetime-local"
                                                    value={form.schedule?.end_date || ''}
                                                    onChange={(e) => handleScheduleChange(index, { end_date: e.target.value })}
                                                />
                                            </div>
                                            
                                            {form.schedule?.type === 'recurring' && (
                                                <div className="schedule-row">
                                                    <label>Repeat every (days):</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={form.schedule?.repeat_every_days || ''}
                                                        onChange={(e) => handleScheduleChange(index, { 
                                                            repeat_every_days: parseInt(e.target.value) || null 
                                                        })}
                                                        placeholder="e.g., 7 for weekly"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Add New Form */}
                <div className="add-form-section">
                    {!showAddForm ? (
                        <button 
                            className="add-form-btn"
                            onClick={() => setShowAddForm(true)}
                            disabled={loading}
                        >
                            <FaPlus />
                            Add Form to Curriculum
                        </button>
                    ) : (
                        <div className="add-form-card">
                            <div className="card-header">
                                <h4>Add New Form</h4>
                                <button 
                                    className="close-btn"
                                    onClick={() => setShowAddForm(false)}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <div className="form-selection">
                                <label>Select Form Template:</label>
                                {loading ? (
                                    <LoadingSpinner text="Loading templates..." variant="inline" size="small" />
                                ) : (
                                    <select
                                        value={newFormData.form_template_id}
                                        onChange={(e) => setNewFormData({
                                            ...newFormData,
                                            form_template_id: e.target.value
                                        })}
                                    >
                                        <option value="">Choose a form template...</option>
                                        {formTemplates
                                            .filter(template => !curriculumForms.some(form => 
                                                form.form_template_id === template._id
                                            ))
                                            .map(template => (
                                                <option key={template._id} value={template._id}>
                                                    {template.name} ({getFormTypeLabel(template.form_type)})
                                                </option>
                                            ))}
                                    </select>
                                )}
                                {selectedTemplateForAdd && (
                                    <small className="targeting-info" style={{ marginTop: '0.5rem', display: 'block' }}>
                                        <FaInfoCircle style={{ marginRight: '0.4rem' }} />
                                        {describeSubmissionPolicy(
                                            selectedTemplateForAdd.form_type,
                                            selectedTemplateForAdd.allow_multiple_submissions
                                        )}
                                    </small>
                                )}
                            </div>

                            <div className="schedule-config">
                                <h5><FaCalendarAlt /> Schedule Configuration</h5>
                                
                                <div className="schedule-grid">
                                    <div className="schedule-item">
                                        <label>Schedule Type:</label>
                                        <select
                                            value={newFormData.schedule.type}
                                            onChange={(e) => setNewFormData({
                                                ...newFormData,
                                                schedule: { ...newFormData.schedule, type: e.target.value }
                                            })}
                                        >
                                            <option value="one_time">One Time</option>
                                            <option value="recurring">Recurring</option>
                                        </select>
                                    </div>
                                    
                                    <div className="schedule-item">
                                        <label>Start Date & Time:</label>
                                        <input
                                            type="datetime-local"
                                            value={newFormData.schedule.start_date}
                                            onChange={(e) => setNewFormData({
                                                ...newFormData,
                                                schedule: { ...newFormData.schedule, start_date: e.target.value }
                                            })}
                                        />
                                    </div>
                                    
                                    <div className="schedule-item">
                                        <label>End Date & Time:</label>
                                        <input
                                            type="datetime-local"
                                            value={newFormData.schedule.end_date}
                                            onChange={(e) => setNewFormData({
                                                ...newFormData,
                                                schedule: { ...newFormData.schedule, end_date: e.target.value }
                                            })}
                                        />
                                    </div>
                                    
                                    {newFormData.schedule.type === 'recurring' && (
                                        <div className="schedule-item">
                                            <label>Repeat Every (days):</label>
                                            <input
                                                type="number"
                                                min="1"
                                                value={newFormData.schedule.repeat_every_days || ''}
                                                onChange={(e) => setNewFormData({
                                                    ...newFormData,
                                                    schedule: { 
                                                        ...newFormData.schedule, 
                                                        repeat_every_days: parseInt(e.target.value) || null 
                                                    }
                                                })}
                                                placeholder="e.g., 7 for weekly"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* NEW: Audience Targeting Section */}
                            <div className="audience-targeting">
                                <h5><FaUsers /> Target Audience</h5>
                                
                                <div className="targeting-config">
                                    <div className="targeting-item">
                                        <label>Targeting Type:</label>
                                        <select
                                            value={newFormData.target_type}
                                            onChange={(e) => setNewFormData({
                                                ...newFormData,
                                                target_type: e.target.value,
                                                target_user_ids: [],
                                                target_filters: {
                                                    account_types: [],
                                                    ranks: [],
                                                    specialities: [],
                                                    institutions: []
                                                }
                                            })}
                                        >
                                            <option value="form_type">Form Type (Auto Student/Preceptor)</option>
                                            <option value="filters">Filter-based Targeting</option>
                                            <option value="explicit">Specific Users</option>
                                        </select>
                                    </div>

                                    {newFormData.target_type === 'filters' && (
                                        <>
                                            <div className="targeting-item">
                                                <label>Account Types:</label>
                                                <div style={{ display: 'flex', gap: '1rem' }}>
                                                    {['student', 'preceptor'].map(type => (
                                                        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={newFormData.target_filters.account_types.includes(type)}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    setNewFormData({
                                                                        ...newFormData,
                                                                        target_filters: {
                                                                            ...newFormData.target_filters,
                                                                            account_types: checked
                                                                                ? [...newFormData.target_filters.account_types, type]
                                                                                : newFormData.target_filters.account_types.filter(t => t !== type)
                                                                        }
                                                                    });
                                                                }}
                                                            />
                                                            <span style={{ textTransform: 'capitalize' }}>{type}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                            
                                            <div className="targeting-item">
                                                <label>Ranks (comma-separated):</label>
                                                <input
                                                    type="text"
                                                    placeholder="e.g., PharmD, SPEP, PGY1"
                                                    value={newFormData.target_filters.ranks.join(', ')}
                                                    onChange={(e) => {
                                                        const ranks = e.target.value.split(',').map(r => r.trim()).filter(r => r);
                                                        setNewFormData({
                                                            ...newFormData,
                                                            target_filters: {
                                                                ...newFormData.target_filters,
                                                                ranks
                                                            }
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {newFormData.target_type === 'explicit' && (
                                        <div className="targeting-item">
                                            <label>User IDs (comma-separated):</label>
                                            <textarea
                                                rows={3}
                                                placeholder="Enter user IDs separated by commas"
                                                value={newFormData.target_user_ids.join(', ')}
                                                onChange={(e) => {
                                                    const ids = e.target.value.split(',').map(id => id.trim()).filter(id => id);
                                                    setNewFormData({
                                                        ...newFormData,
                                                        target_user_ids: ids
                                                    });
                                                }}
                                                style={{ fontFamily: 'monospace' }}
                                            />
                                            <small style={{ color: '#6b7280' }}>
                                                Selected: {newFormData.target_user_ids.length} user(s)
                                            </small>
                                        </div>
                                    )}

                                    {newFormData.target_type === 'form_type' && (
                                        <div className="targeting-info">
                                            <FaInfoCircle style={{ marginRight: '0.5rem' }} />
                                            This form will be automatically targeted based on the form type (student or preceptor forms).
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-actions">
                                <button 
                                    className="cancel-btn"
                                    onClick={() => setShowAddForm(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="add-btn"
                                    onClick={handleAddForm}
                                >
                                    Add Form
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {canSkip && (
                    <div className="skip-section">
                        <div className="skip-info">
                            <span>Forms can be configured later if needed</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CurriculumFormManager;
